import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { HttpService } from './http.service';
import { environment } from '../../../environments/environment';
import { TableListOptions } from '../types/table-list.types';
import { ReportType } from '../types/export.types';

@Injectable({
  providedIn: 'root',
})
export class ExportService {
  baseUrl: any;
  EXPORT_URL: any;
  REPORTS_URL: any;
  DOWNLOAD_URL: any;
  DAILY_VOLUME: any;
  WEEKLY_SPREADS: any;
  WEEKLY_SPREADS_LPS: any;
  PRICE_IMPROVEMENT: any;
  BUSINESS_REPORT_EXPORT: any;
  FILL_RATES_BY_DC: any;
  FILL_RATES_RFE_BY_DC: any;
  CLIENT_VOLUME_BY_SYMBOL: any;
  SYMBOL_VOLUME: any;
  TOP_SELLERS: any;

  constructor(private httpService: HttpService) {
    this.baseUrl = environment.mexExchange.api.baseUrl;
    this.EXPORT_URL = this.baseUrl + environment.mexExchange.api.endpoints.reports.export;
    this.REPORTS_URL = this.baseUrl + environment.mexExchange.api.endpoints.reports.list;
    this.DOWNLOAD_URL = this.baseUrl + environment.mexExchange.api.endpoints.reports.download;
    this.DAILY_VOLUME = this.baseUrl + environment.mexExchange.api.endpoints.reports.dailyVolume;
    this.WEEKLY_SPREADS = this.baseUrl + environment.mexExchange.api.endpoints.reports.weeklySpreads;
    this.WEEKLY_SPREADS_LPS = this.baseUrl + environment.mexExchange.api.endpoints.reports.weeklySpreadsLPs;
    this.PRICE_IMPROVEMENT = this.baseUrl + environment.mexExchange.api.endpoints.reports.priceImprovement;
    this.BUSINESS_REPORT_EXPORT = this.baseUrl + environment.mexExchange.api.endpoints.reports.businessReportExport;
    this.FILL_RATES_BY_DC = this.baseUrl + environment.mexExchange.api.endpoints.reports.fillRatesByDC;
    this.FILL_RATES_RFE_BY_DC = this.baseUrl + environment.mexExchange.api.endpoints.reports.fillRatesRFEByDC;
    this.CLIENT_VOLUME_BY_SYMBOL = this.baseUrl + environment.mexExchange.api.endpoints.reports.clientVolumeBySymbol;
    this.SYMBOL_VOLUME = this.baseUrl + environment.mexExchange.api.endpoints.reports.symbolVolume;
    this.TOP_SELLERS = this.baseUrl + environment.mexExchange.api.endpoints.reports.topSellers;
  }
 
  export(filterOptions: TableListOptions = {}, entityType: string): Observable<any> {
    let parameters = Object.assign({}, filterOptions);
    // removing unneeded Listing Params
    delete parameters.sort;
    delete parameters.page;
    delete parameters.size;

    let queryParams = this.httpService.getListingParams(parameters);
    // removing date parameters after they have been already formatted properly
    delete parameters.startDate;
    delete parameters.endDate;
    delete queryParams.page;

    if (parameters.searchTerm) {
      queryParams.search = parameters.searchTerm;
      delete parameters.searchTerm;
    }

    queryParams = { ...queryParams, ...parameters };
    const headers = this.httpService.getHeaders();
    const params = this.httpService.getSearchParams(queryParams);
    const options = this.httpService.getOptions(headers, params);
    const url = this.EXPORT_URL.replace(':entityType', entityType);

    return this.httpService.get(url, options, { fullBody: true }).pipe(
      map((res) => {
        return res;
      })
    );
  }

  getReports(queryParams: any): Observable<any> {
    const headers = this.httpService.getHeaders();
    const params = this.httpService.getSearchParams(queryParams);
    const options = this.httpService.getOptions(headers, params);

    return this.httpService.get(this.REPORTS_URL, options, { fullBody: true }).pipe(
      map((t) => {
        return t;
      })
    );
  }

  downloadReport(reportKey: string): Observable<any> {
    const headers = this.httpService.getHeaders();
    const params = this.httpService.getSearchParams({});
    const options = this.httpService.getOptions(headers, params);
    const url = this.DOWNLOAD_URL.replace(':key', encodeURIComponent(reportKey));

    return this.httpService.get(url, options, { fullBody: true }).pipe(
      map((response) => {
        const link = document.createElement("a");
        link.href = response.url;
        link.download = reportKey;
        link.click();
      })
    );
  }

  getReportByType(reportType: ReportType, queryParams: any): Observable<any> {
    const headers = this.httpService.getHeaders();
    const params = this.httpService.getSearchParams(queryParams);
    const options = this.httpService.getOptions(headers, params);

    let url;
    switch (reportType) {
      case ReportType.DAILY_VOLUME: {
        url = this.DAILY_VOLUME;
        break;
      }
      case ReportType.WEEKLY_SPREADS: {
        url = this.WEEKLY_SPREADS;
        break;
      }
      case ReportType.PRICE_IMPROVEMENT: {
        url = this.PRICE_IMPROVEMENT;
        break;
      }
      case ReportType.FILL_RATE_BY_DC: {
        url = this.FILL_RATES_BY_DC;
        break;
      }
      case ReportType.CLIENT_VOLUME_SYMBOL: {
        url = this.CLIENT_VOLUME_BY_SYMBOL;
        break;
      }
      case ReportType.SYMBOL_VOLUME: {
        url = this.SYMBOL_VOLUME;
        break;
      }
      case ReportType.TOP_SELLERS: {
        url = this.TOP_SELLERS;
        break;
      }
    }

    return this.httpService.get(url, options, { fullBody: true, timeout: 60000 }).pipe(
      map((t) => {
        return t;
      })
    );
  }

  exportReportByType(reportType: ReportType, queryParams: any): Observable<any> {
    const headers = this.httpService.getHeaders();
    const params = this.httpService.getSearchParams(queryParams);
    const options = this.httpService.getOptions(headers, params);
    const url = this.BUSINESS_REPORT_EXPORT.replace(':reportType', reportType.toLowerCase());

    return this.httpService.get(url, options, { fullBody: true }).pipe(
      map((t) => {
        return t;
      })
    );
  }

  getRfeByTakerDC(dealCode: string, queryParams: any): Observable<any> {
    const headers = this.httpService.getHeaders();
    const params = this.httpService.getSearchParams(queryParams);
    const options = this.httpService.getOptions(headers, params); 
    const url = this.FILL_RATES_RFE_BY_DC.replace(':dealCode', dealCode);
    return this.httpService.get(url, options, { fullBody: true, timeout: 60000 }).pipe(
      map((t) => {
        return t;
      })
    );
  }

  getWeeklySpreadsLPs(): Observable<any> {
    const headers = this.httpService.getHeaders();
    const options = this.httpService.getOptions(headers);

    return this.httpService.get(this.WEEKLY_SPREADS_LPS, options, { fullBody: true }).pipe(
      map((t) => {
        return t;
      })
    );
  }
}